import React from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HtmlRenderer } from './HtmlRenderer';
import { AppColors, typographyClamps } from '../utils/utils';

interface SectionViewProps {
  idx: number;
  label: string;
  title?: string;
  subtitle?: string;
  features: string[];
  footerText: string;
  imagePath: string;
  labelIconPath?: string;
  footerIconPath?: string;
  imageBGPath?: string;
  imageAlign?: 'start' | 'center' | 'end';
  isForFeature?: boolean;
  titleTextColor?: string;
  transparentBackground?: boolean;
  isSmallImage?: boolean;
  openImageViewView?: (link: string, event?: React.MouseEvent<HTMLImageElement>) => void;
  openGroupImageView?: (event: React.MouseEvent<HTMLImageElement>) => void;
}

export const LotSectionView: React.FC<SectionViewProps> = ({
  idx,
  label,
  title,
  subtitle,
  features,
  footerText,
  imagePath,
  labelIconPath,
  footerIconPath,
  imageBGPath,
  imageAlign,
  isForFeature,
  titleTextColor,
  transparentBackground,
  isSmallImage,
  openImageViewView,
  openGroupImageView,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const isImageOnRight = idx % 2 !== 0;

  const gridSize = isForFeature
    ? { xs: 12, sm: 5.5, md: 5.5 }
    : { xs: 5.5, sm: 5.5, md: 5.5 };

  const getScreenshotView = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: imageAlign ?? 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {imageBGPath && (
        <Box
          component='img'
          src={imageBGPath}
          sx={{
            position: 'absolute',
            display: 'block',
            right: {
              sm: 0,
              md: isImageOnRight ? (imageAlign === 'end' ? '-60%' : '-30%') : 0,
            },
            left: { sm: 0, md: isImageOnRight ? 0 : '-30%' },
            height: isMobile ? '100%' : '100%',
            width: isMobile ? '100%' : 'auto',
            // rotate: idx === 1 ? '30deg' : idx === 2 ? '-150deg' : '0deg',
          }}
        />
      )}

      <Box
        component='img'
        src={imagePath}
        // width={{
        //   xs: isForFeature ? '80%' : '100%',
        //   sm: '100%',
        //   md: '80%',
        //   lg: '70%',
        // }}
        maxWidth={{ xs: '100%', sm: '100%', lg: isSmallImage ? '80%' : '60%' }}
        height='auto'
        // maxHeight={'270px'}
        // maxWidth={'270px'}
        sx={{ position: 'relative', cursor: 'pointer' }}
        onClick={(e) => {
          openImageViewView && openImageViewView((e.currentTarget as HTMLImageElement).src, e);
          openGroupImageView && openGroupImageView(e);
        }}
      />
    </Box>
  );

  const getFeatureListView = (list: string[]) => {
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='start'
        gap={{ xs: 1, sm: 2 }}
      >
        {list.map((item, index) => (
          <Stack
            key={index}
            direction='row'
            alignItems='start'
            // py={isMobile ? 0.5 : 1}
          >
            <Typography
              sx={{
                color: titleTextColor ?? AppColors.grayColor,
                fontSize:
                  isForFeature || !isMobile
                    ? typographyClamps.fontSize17
                    : typographyClamps.fontSize15,
                // lineHeight: 1.2,
                '& span': {
                  // background: AppColors.aiGradient,
                  // WebkitBackgroundClip: 'text',
                  // color: 'transparent',
                  fontWeight: 'bold',
                },
              }}
            >
              <HtmlRenderer htmlString={item} />
            </Typography>
          </Stack>
        ))}
      </Box>
    );
  };

  return (
    <Box
      // py={isForFeature ? 0 : 3}
      // my={3}
      sx={{
        background: !isImageOnRight && idx !== 0 ? AppColors.aiGradient8 : null,
      }}
    >
      <Grid
        container
        gap={{ sx: 0, sm: 5, md: 5 }}
        direction='row'
        justifyContent='space-between'
        alignItems={labelIconPath ? 'start' : 'center'}
        py={3}
        paddingLeft={{ xs: 2, sm: '5%', md: '8%', lg: '15%' }}
        paddingRight={{ xs: 2, sm: '5%', md: '8%', lg: '15%' }}
        sx={
          {
            // background: transparentBackground ? undefined : AppColors.aiGradient,
          }
        }
      >
        {/* {!isMobile && !isImageOnRight && ( */}
        {!(isForFeature && isMobile) && !isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}

        <Grid size={gridSize}>
          <Box
            width={'100%'}
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
          >
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                justifyContent: isMobile ? 'space-evenly' : 'space-between',
                alignItems: isMobile ? 'center' : 'flex-end',
              }}
            >
              <Typography
                component='p'
                sx={{
                  // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                  fontSize: {
                    xs: typographyClamps.h6,
                    sm: typographyClamps.h5,
                    md: typographyClamps.h4,
                  },
                  fontWeight: 'bold',
                  textAlign: isMobile ? 'center' : 'start',
                  my: 1,
                  '& span': {
                    background: AppColors.textGradient,
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  },
                }}
              >
                {label}
              </Typography>
              {labelIconPath && (
                <Box
                  component={'img'}
                  src={labelIconPath}
                  sx={{ maxHeight: isMobile ? '10vh' : '20vh' }}
                />
              )}
            </Stack>

            {title && (
              <Typography
                component='p'
                sx={{
                  // fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                  fontSize: {
                    xs: typographyClamps.caption,
                    sm: typographyClamps.h6,
                    md: typographyClamps.h5,
                  },
                  fontWeight: 'bold',
                  color: titleTextColor ?? AppColors.whiteColor,
                  textAlign: isMobile ? 'center' : 'start',
                  width: '100%',
                  my: 1,
                  '& span': {
                    background: AppColors.textGradient,
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                  },
                }}
              >
                {title}
              </Typography>
            )}

            {subtitle && (
              <Typography
                component='p'
                textAlign={{ xs: 'center', sm: 'center', md: 'start' }}
                fontWeight={400}
                py={1}
                color='#6B727A'
              >
                <HtmlRenderer htmlString={subtitle} />
              </Typography>
            )}

            {isForFeature && isMobile && (
              <Box py={2}>{getScreenshotView()}</Box>
            )}

            {features && getFeatureListView(features)}

            {footerText && (
              <Typography
                sx={{
                  mt: 2,
                  py: 1,
                  color: AppColors.greenStrokeColor,
                  fontSize: isMobile
                    ? typographyClamps.fontSize15
                    : typographyClamps.fontSize17,
                  fontWeight: 600,
                }}
              >
                Result:
                <HtmlRenderer htmlString={footerText} />
              </Typography>
            )}

            {footerIconPath && (
              <Box width={'100%'} textAlign={'center'} mt={4}>
                <Box
                  component={'img'}
                  src={footerIconPath}
                  sx={{ maxHeight: isMobile ? '10vh' : '20vh' }}
                />
              </Box>
            )}
          </Box>
        </Grid>

        {/* {!isMobile && isImageOnRight && ( */}
        {!(isForFeature && isMobile) && isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}
      </Grid>
    </Box>
  );
};
